import PasswordField from "@components/primitives/PasswordField";
import getChangePasswordSchema from "@lib/validators/user/changePasswordSchema";
import { Button } from "@ecco/ui/button";
import { FormControl } from "@ecco/ui/form-control";
import { FormErrorMessage } from "@ecco/ui/form-error";
import { FormLabel } from "@ecco/ui/form-label";
import { Flex } from "@ecco/ui/flex";

import { Form, Formik } from "formik";
import { styles } from "./ChangePasswordForm.style";
import { FORM_ID, initialValues } from "./constants";
import type { Headers, Values } from "./types";
import { useChangePasswordForm } from "./useChangePasswordForm";

export const changePasswordProfileWrapper = "change-password-profile-wrapper";

type Props = {
    handleClose?: () => void;
    headers: Headers;
};

export const ChangePasswordForm = ({ handleClose, headers }: Props) => {
    const { errorHeaders, onClose, submitHandler } = useChangePasswordForm({
        headers,
        handleClose,
    });

    return (
        <>
            <Formik<Values>
                initialValues={initialValues}
                validationSchema={getChangePasswordSchema(errorHeaders, {
                    currentPassword: headers.currentPassword,
                    newPassword: headers.newPassword,
                    confirmPassword: headers.confirmPassword,
                })}
                onSubmit={submitHandler}
            >
                {({ submitForm, errors, isSubmitting, touched }) => {
                    return (
                        <Form noValidate={true} id={FORM_ID} data-testid="SignupFieldsForm">
                            <FormControl
                                sx={styles.input}
                                isRequired
                                isInvalid={Boolean(
                                    errors?.currentPassword && touched.currentPassword
                                )}
                            >
                                <FormLabel htmlFor="password" id="currentPasswordLabel">
                                    {headers.currentPassword}
                                </FormLabel>
                                <PasswordField
                                    id="currentPassword"
                                    ariaLabelledBy="currentPasswordLabel"
                                />

                                <FormErrorMessage>
                                    {errors?.currentPassword?.toString()}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                sx={styles.input}
                                isRequired
                                isInvalid={Boolean(errors?.newPassword && touched.newPassword)}
                            >
                                <FormLabel htmlFor="newPassword" id="newPasswordLabel">
                                    {headers.newPassword}
                                </FormLabel>
                                <PasswordField id="newPassword" ariaLabelledBy="newPasswordLabel" />
                                <FormLabel fontSize="desktopBodyTextTiny" fontWeight="normal">
                                    {headers.passwordRules}
                                </FormLabel>
                                <FormErrorMessage>
                                    {errors.newPassword?.toString()}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                sx={styles.input}
                                isRequired
                                isInvalid={Boolean(
                                    errors?.confirmPassword && touched.confirmPassword
                                )}
                            >
                                <FormLabel htmlFor="password" id="confirmPasswordLabel">
                                    {headers.confirmPassword}
                                </FormLabel>
                                <PasswordField
                                    id="confirmPassword"
                                    ariaLabelledBy="passwordLabel"
                                />

                                <FormErrorMessage>
                                    {errors.confirmPassword?.toString()}
                                </FormErrorMessage>
                            </FormControl>

                            <Flex mt={4} gap={0.2}>
                                <Button
                                    sx={styles.button}
                                    onClick={onClose}
                                    data-testid="createAccountButton"
                                    variant="tertiary"
                                >
                                    {headers.cancel}
                                </Button>
                                <Button
                                    sx={styles.button}
                                    form={FORM_ID}
                                    onClick={submitForm}
                                    type="submit"
                                    isLoading={isSubmitting}
                                    data-testid="createAccountButton"
                                >
                                    {headers.savePassword}
                                </Button>
                            </Flex>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};
